import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/apiUtils';
import { Card, CardHeader, CardContent, Typography, Box, CircularProgress, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const GroomerPerformance = ({ refreshKey }) => {
  const [data, setData] = useState([]);
  const [mtdData, setMtdData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Calculate dates for Month-To-Date (MTD) calculations
        const today = new Date();
        
        // Get first day of current month (midnight local time)
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        
        // Get yesterday's date (midnight local time)
        // Using proper date subtraction to handle month boundaries correctly
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0); // Set to midnight
        
        // Format dates as YYYY-MM-DD using local timezone components
        // This avoids timezone issues with toISOString() which uses UTC
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          // Month is 0-indexed in JS, so add 1 and pad with leading zero if needed
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDate = formatLocalDate(firstDayOfMonth);
        const endDate = formatLocalDate(yesterday);
        
        console.log(`Today is: ${formatLocalDate(today)}`);
        console.log(`MTD date range using local timezone: ${startDate} to ${endDate}`);

        // Fetch today's data
        const todayResponse = await apiFetch('/api/reports/groomer-performance');
        if (!todayResponse.ok) {
          throw new Error(`Failed to fetch today's groomer performance data: ${todayResponse.status} ${todayResponse.statusText}`);
        }
        
        const todayResult = await todayResponse.json();
        console.log('Today\'s groomer performance data:', todayResult);
        setData(todayResult);

        // Fetch MTD data
        console.log(`Fetching MTD data from ${startDate} to ${endDate}...`);
        const mtdResponse = await apiFetch(`/api/reports/groomer-performance?start_date=${startDate}&end_date=${endDate}`);
        if (!mtdResponse.ok) {
          throw new Error(`Failed to fetch MTD groomer performance data: ${mtdResponse.status} ${mtdResponse.statusText}`);
        }
        
        const mtdResult = await mtdResponse.json();
        console.log('MTD groomer performance data:', mtdResult);
        setMtdData(mtdResult);
      } catch (err) {
        console.error('Error fetching groomer performance data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]); // Add refreshKey to the dependency array

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Calculate total sum of all groomer totals (for revenue)
  const calculateTotalSum = () => {
    if (data.length === 0) return 0;
    return data.reduce((sum, row) => sum + (row.totalCollectedPriceToday || 0), 0);
  };
  
  // Calculate MTD metrics
  const calculateMtdMetrics = () => {
    if (!mtdData || !Array.isArray(mtdData) || mtdData.length === 0) {
      return {
        mtdTotal: 0,
        mtdAvgDaily: 0,
        projectedMonthTotal: 0
      };
    }
    
    // Calculate MTD total revenue by summing up totalCollectedPriceToday across all groomers
    // This represents the total collected for all services from the first day of the month to yesterday
    const mtdTotal = mtdData.reduce((total, groomer) => {
      return total + (groomer.totalCollectedPriceToday || 0);
    }, 0);
    
    // Calculate days in the month
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    
    // Calculate number of days elapsed so far this month (excluding today)
    const dayOfMonth = today.getDate() - 1; // Subtract 1 to exclude today
    const daysElapsed = dayOfMonth > 0 ? dayOfMonth : 1; // Ensure we don't divide by zero
    
    // Calculate MTD average daily services revenue (total divided by days elapsed)
    const mtdAvgDaily = mtdTotal / daysElapsed;
    
    // Calculate projected month total services revenue (daily average × days in month)
    const projectedMonthTotal = mtdAvgDaily * daysInMonth;
    
    console.log('MTD Calculations:', { 
      mtdTotal, 
      daysElapsed, 
      mtdAvgDaily, 
      daysInMonth, 
      projectedMonthTotal,
      groomers: mtdData.length
    });
    
    return {
      mtdTotal,
      mtdAvgDaily,
      projectedMonthTotal
    };
  };
  
  // Calculate the MTD metrics
  const mtdMetrics = calculateMtdMetrics();

  if (loading) {
    return (
      <Card elevation={3} sx={{ height: '400px', ml: 2 }}>
        <CardHeader
          title="Today's Groomer Performance"
          titleTypographyProps={{ 
            align: 'center', 
            variant: 'h6',
            sx: { fontSize: '1rem', fontWeight: 'bold', color: 'white' }
          }}
          sx={{ bgcolor: '#1976d2' }}
        />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '320px' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={3} sx={{ height: '400px', ml: 2 }}>
        <CardHeader
          title="Today's Groomer Performance"
          titleTypographyProps={{ 
            align: 'center', 
            variant: 'h6',
            sx: { fontSize: '1rem', fontWeight: 'bold', color: 'white' }
          }}
          sx={{ bgcolor: '#1976d2' }}
        />
        <CardContent sx={{ height: '320px' }}>
          <Typography color="error" variant="body1">
            Error loading groomer performance data: {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card elevation={3} sx={{ height: '380px', ml: 2 }}>
        <CardHeader
          title="Today's Groomer Performance"
          titleTypographyProps={{ 
            align: 'center', 
            variant: 'h6',
            sx: { fontSize: '1rem', fontWeight: 'bold', color: 'white' }
          }}
          sx={{ bgcolor: '#1976d2' }}
        />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '320px' }}>
          <Typography variant="body1" color="text.secondary">
            No groomer performance data available for today
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={3} sx={{ height: { xs: 520, sm: 380 }, ml: 2, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: '100%', gap: { xs: 1, sm: 2 } }}>
            {/* Card Title - for mobile, moved to top */}
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 'bold', 
                color: 'white', 
                textAlign: { xs: 'center', sm: 'right' }, 
                display: { xs: 'block', sm: 'none' }, 
                mb: 1, 
                width: '100%' 
              }}
            >
              Today's Groomer Performance
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, width: { xs: '100%', sm: 'auto' } }}>
              {/* Today's Services Total */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', mr: 1 }}>
                  {formatCurrency(calculateTotalSum())}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                  Today's Services Total
                </Typography>
              </Box>

              {/* MTD Services Total */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', mr: 1 }}>
                  {formatCurrency(mtdMetrics.mtdTotal)}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                  MTD Services Total
                </Typography>
              </Box>

              {/* MTD Services Daily Avg */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', mr: 1 }}>
                  {formatCurrency(mtdMetrics.mtdAvgDaily)}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                  MTD Services Daily Avg
                </Typography>
              </Box>

              {/* Projected Services Monthly Total */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', mr: 1 }}>
                  {formatCurrency(mtdMetrics.projectedMonthTotal)}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'white', fontSize: '0.8rem' }}>
                  Projected Services Monthly Total
                </Typography>
              </Box>
            </Box>

            {/* Card Title - for desktop, remains at right */}
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 'bold', 
                color: 'white', 
                textAlign: 'right', 
                display: { xs: 'none', sm: 'block' } 
              }}
            >
              Today's Groomer Performance
            </Typography>
          </Box>
        }
        titleTypographyProps={{ component: 'div' }}
        sx={{ 
          bgcolor: '#1976d2', 
          padding: { xs: '8px', sm: '12px' }, 
          '& .MuiCardHeader-content': { width: '100%' } 
        }}
      />
      <CardContent sx={{ p: 1, height: '320px' }}>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '100%', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Groomer</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Appts</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Pets</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Avg Groom</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' } }}>
                  <TableCell component="th" scope="row">
                    {row.staffName || 'Unknown'}
                  </TableCell>
                  <TableCell align="right">{row.numCompletedAppointmentsToday}</TableCell>
                  <TableCell align="right">{row.numCompletedPetsToday}</TableCell>
                  <TableCell align="right">{formatCurrency(row.totalCollectedPriceToday)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.averageGroomPriceToday)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default GroomerPerformance;